import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactFlowProvider, type Edge, type Node } from "reactflow";
import { EntityCard, EntityCardRef, type BaseEntity, type EntityCardProps } from "components/Entities/EntityCardBase";
import InputTextArea from "ui/InputTextArea";
import LibraryCollectionViewer from "components/Collections/LibraryCollectionViewer";
import LibraryCollectionForm, {
  type LibraryDraftItem,
} from "components/Posts/LibraryCollectionForm";
import GraphCanvas from "components/Graph/GraphCanvas";
import GraphFullscreenViewer from "components/Graph/GraphFullscreenViewer";
import type { GraphIssueNodeData } from "components/Graph/GraphIssueNodeTypes";
import type { GraphCanvasEdgeData } from "lib/api/graphCollections";
import { getCollectionUiStrings, resolveCollectionLocale } from "i18n/collectionUi";
import { formatGraphMessage, graphMessage, resolveGraphLocale } from "components/Graph/graphCopy";
import { useGraphSnapshotQuery } from "components/Collections/useCollections";
import PostKindChip from "components/Posts/display/PostKindChip";
import { Chip } from "ui/Chip";
import { GraphCanvasSkeleton } from "ui/LoadingSkeleton";

export type CollectionEntity = BaseEntity & {
  title?: string;
  kind?: "collection";
  properties?: NonNullable<BaseEntity["properties"]> & {
    collection_type?: string;
  };
};

export interface CollectionGraphSnapshotData {
  nodes: Array<Node<GraphIssueNodeData>>;
  edges: Array<Edge<GraphCanvasEdgeData>>;
  /**
   * React Flow node id of the graph's seed/root issue. Used as the
   * recenter target so "Recenter" returns the user to the starting node
   * rather than the empty origin on user-positioned graphs.
   */
  rootNodeId?: string | null;
}

interface CollectionCardRefactoredProps
  extends Omit<
    EntityCardProps<CollectionEntity>,
    | "entity"
    | "entityType"
    | "renderTags"
    | "renderAdditionalContent"
    | "renderEditFields"
    | "renderActions"
    | "renderPrimaryContent"
  > {
  collection: CollectionEntity;
  referenceLink?: string;
  onReferenceLinkChange?: (value: string) => void;
  libraryItems?: LibraryDraftItem[];
  onLibraryItemsChange?: (items: LibraryDraftItem[]) => void;
  /**
   * Snapshot of the collection's graph. When provided for a "graph" collection
   * type, the GraphCanvas is rendered as the primary visual block in the card,
   * above the normal media/url preview section.
   */
  graphSnapshot?: CollectionGraphSnapshotData | null;
  /**
   * When `true` and the collection is a graph, the card renders the graph
   * canvas skeleton in place of the real canvas so the user sees the graph
   * chrome (header, chips, title, canvas frame, action bar) immediately
   * rather than a missing primary visual while the snapshot is in flight.
   */
  graphSnapshotLoading?: boolean;
  /**
   * `"full"` (default) renders the normal EntityCard layout with all chrome,
   * media, comments, votes, etc. `"compact"` renders a trimmed card (header,
   * meta, small graph preview, optional action slot) suitable for Extend /
   * Remix source pickers and the selected-sources strip. Comments, votes,
   * and description are suppressed in compact mode because the card is
   * picking-oriented, not consuming-oriented.
   */
  variant?: "full" | "compact";
  /**
   * Optional action node anchored at the top-right of the compact card. Used
   * by the Extend/Remix picker for the per-result "Add" button and by the
   * selected-sources strip for the "Remove" (X) button. Ignored for the
   * full variant (pass `onEdit` / `onDelete` to EntityCard instead).
   */
  action?: React.ReactNode;
}

const noopDepth = () => undefined;
const noopNodesChange = () => undefined;
const noopEdgesChange = () => undefined;

function normalizeCollectionType(value: unknown): "standard" | "graph" | "library" {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "graph") {
    return "graph";
  }
  if (normalized === "library") {
    return "library";
  }
  return "standard";
}

const CollectionCardRefactored = forwardRef<EntityCardRef, CollectionCardRefactoredProps>(
  (
    {
      collection,
      referenceLink,
      onReferenceLinkChange,
      libraryItems,
      onLibraryItemsChange,
      graphSnapshot,
      graphSnapshotLoading = false,
      variant = "full",
      action,
      ...restProps
    },
    ref
  ) => {
    const navigate = useNavigate();
    const ui = getCollectionUiStrings(resolveCollectionLocale());
    const collectionType = normalizeCollectionType(collection.properties?.collection_type);
    const graphLocale = useMemo(
      () =>
        resolveGraphLocale(
          typeof navigator !== "undefined" ? navigator.language : undefined
        ),
      []
    );
    /*
     * Full-screen graph viewer is opened when the user taps/clicks the
     * thumbnail. Kept as local state so every card has its own viewer
     * instance — important for feeds that mount many cards at once.
     */
    const [isFullscreenViewerOpen, setFullscreenViewerOpen] = useState(false);

    /**
     * If the parent did not pre-provide a `graphSnapshot` (e.g. generic
     * EntitiesList, search results), we fetch it here so the card is
     * self-contained. The feed passes the pre-mapped snapshot from the
     * collection feed API to avoid duplicate network requests.
     */
    const graphSnapshotQuery = useGraphSnapshotQuery(
      collectionType === "graph" && !graphSnapshot && collection.id
        ? Number(collection.id)
        : null
    );

    const effectiveGraphSnapshot = graphSnapshot ?? graphSnapshotQuery.data ?? null;
    const isGraphSnapshotLoading =
      collectionType === "graph" &&
      !graphSnapshot &&
      (graphSnapshotQuery.isLoading || graphSnapshotQuery.isFetching);

    
    // Render collection-specific tags
    const renderTags = (_entity: BaseEntity) => {
      return (
        <>
          <PostKindChip kind="collection" className="bg-eoe-entity-collection text-white" />
          <Chip size="sm" className="bg-eoe-icon dark:bg-eoe-dark-icon text-eoe-text dark:text-eoe-dark-text">
            {collectionType === "library"
              ? ui.collectionKindLibrary
              : collectionType === "graph"
                ? ui.collectionKindGraph
                : ui.collectionKindStandard}
          </Chip>
        </>
      );
    };

    const renderAdditionalContent = (_entity: BaseEntity) => {
      if (collectionType !== "library" || restProps.editMode) {
        return null;
      }
      const collectionId = Number.parseInt(String(collection.id), 10);
      if (!Number.isFinite(collectionId) || collectionId <= 0) {
        return null;
      }
      return <LibraryCollectionViewer collectionId={collectionId} />;
    };

    const renderEditFields = (_entity: BaseEntity) => {
      if (!restProps.editMode) {
        return null;
      }
      if (collectionType === "library" && libraryItems && onLibraryItemsChange) {
        const audience =
          typeof collection.properties?.privacy === "string"
            ? collection.properties.privacy
            : "public";
        return (
          <LibraryCollectionForm
            items={libraryItems}
            onItemsChange={onLibraryItemsChange}
            audience={audience}
            disabled={false}
          />
        );
      }
      if (collectionType === "graph") {
        // For graph collections, the full editor is opened via onEdit instead of inline fields.
        // The inline edit fields are only for standard/library. The graph editor is a dedicated
        // full-screen surface with canvas + details.
        return null;
      }
      return (
        <InputTextArea
          id="collection-reference-link"
          name="collection-reference-link"
          labelText={ui.referenceUrlLabel}
          value={referenceLink || ""}
          onChange={(event) => onReferenceLinkChange?.(event.target.value)}
          autoComplete="url"
          spellCheck={false}
        />
      );
    };

    const hasGraphSnapshot = !!effectiveGraphSnapshot && effectiveGraphSnapshot.nodes.length > 0;
    const shouldShowGraphCanvas = collectionType === "graph" && hasGraphSnapshot;
    /**
     * Surface the graph skeleton whenever we know this is a graph collection
     * but the snapshot has not resolved yet. Suppressed in edit mode because
     * the editor owns its own loading chrome and should not be confused by a
     * read-only skeleton.
     *
     * `isGraphSnapshotLoading` comes from the internal query (when parent did
     * not pre-provide a snapshot). The optional `graphSnapshotLoading` prop
     * allows parents that manage their own loading state (e.g. CollectionProfile)
     * to force the skeleton.
     */
    const shouldShowGraphSkeleton =
      collectionType === "graph" &&
      !restProps.editMode &&
      (isGraphSnapshotLoading || graphSnapshotLoading) &&
      !hasGraphSnapshot;

    const renderPrimaryContent = shouldShowGraphCanvas
      ? () => (
          /*
           * Feed / profile thumbnail: presentation-only canvas with a
           * button overlay on top. The underlying GraphCanvas has all
           * interactions disabled (pan/zoom/drag/edge-click), so the click
           * handler on the wrapping button reliably opens the full-screen
           * viewer instead of being eaten by React Flow. Keeping each
           * canvas inside its own ReactFlowProvider isolates state across
           * cards in the same feed.
           */
          <div className="graph-container relative h-[50vh] min-h-[320px] w-full overflow-hidden">
            <div className="pointer-events-none absolute inset-0">
              <ReactFlowProvider>
                <GraphCanvas
                  nodes={effectiveGraphSnapshot!.nodes}
                  edges={effectiveGraphSnapshot!.edges}
                  onNodesChange={noopNodesChange}
                  onEdgesChange={noopEdgesChange}
                  depth={0}
                  onDepthUp={noopDepth}
                  onDepthDown={noopDepth}
                  readOnly
                  presentationOnly
                  nodeOrientation="vertical"
                  locale={graphLocale}
                  rootNodeId={effectiveGraphSnapshot!.rootNodeId ?? null}
                />
              </ReactFlowProvider>
            </div>
            <button
              type="button"
              className="absolute inset-0 z-10 h-full w-full cursor-zoom-in bg-transparent"
              aria-label={formatGraphMessage(graphLocale, "openFullscreenGraphAria", {
                title:
                  typeof collection.title === "string" && collection.title.trim().length > 0
                    ? collection.title
                    : graphMessage(graphLocale, "untitledGraph"),
              })}
              onClick={() => setFullscreenViewerOpen(true)}
            />
          </div>
        )
      : shouldShowGraphSkeleton
        ? () => (
            <div className="graph-container h-[50vh] min-h-[320px] w-full overflow-hidden">
              <GraphCanvasSkeleton
                ariaLabel={graphMessage(graphLocale, "loadingGraph")}
              />
            </div>
          )
        : undefined;

    /**
     * Graph collections have their own full-fidelity editor (canvas + details +
     * draft/publish actions) that lives at `/graph?mode=edit&collectionId=<id>`.
     * Route there instead of the generic entity edit flow so the user gets the
     * interactive graph canvas, not just the details modal.
     */
    const handleEdit = useCallback((): void => {
      if (collectionType === "graph") {
        const collectionId = Number(collection.id);
        if (Number.isFinite(collectionId) && collectionId > 0) {
          navigate(`/graph?mode=edit&collectionId=${collectionId}`);
          return;
        }
      }
      if (restProps.onEdit) {
        restProps.onEdit();
      }
    }, [collectionType, collection.id, navigate, restProps]);

    if (variant === "compact") {
      // Compact card: header (avatar + title/actor), small graph preview if
      // available, optional action slot in the top-right. No body, comments,
      // or votes — this variant is for picking, not consuming.
      const title = typeof collection.title === "string" && collection.title.trim().length > 0
        ? collection.title.trim()
        : null;
      const actorName = collection.properties?.privacy === "anonymous"
        ? null
        : (collection.user_id != null ? String(collection.user_id) : null);
      return (
        <article
          className="collection-card-compact relative flex w-full flex-col overflow-hidden rounded-xl border border-eoe-border-main bg-eoe-bg p-3 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
          aria-label={title ?? graphMessage(graphLocale, "untitledGraph")}
        >
          {action ? (
            <div className="absolute right-2 top-2 z-10">
              {action}
            </div>
          ) : null}
          <div className="mb-2 flex items-start gap-2 pr-10">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-1.5">
                <PostKindChip kind="collection" className="bg-eoe-entity-collection text-white" />
                <Chip size="sm" className="bg-eoe-icon dark:bg-eoe-dark-icon text-eoe-text dark:text-eoe-dark-text">
                  {ui.collectionKindGraph}
                </Chip>
              </div>
              <h3 className="truncate text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {title ?? graphMessage(graphLocale, "untitledGraph")}
              </h3>
              {actorName ? (
                <p className="truncate text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {actorName}
                </p>
              ) : null}
            </div>
          </div>
          {collectionType === "graph" && (hasGraphSnapshot || shouldShowGraphSkeleton) ? (
            <div className="collection-card-compact-canvas h-28 w-full overflow-hidden rounded-md border border-eoe-border-main dark:border-eoe-dark-border-main">
              {hasGraphSnapshot ? (
                /*
                 * Compact-variant thumbnails are presentation-only so the
                 * parent's action slot (Add/X) is the only interactive
                 * element. No overlay click here — the whole card is
                 * already embedded inside a picker flow whose selection
                 * is driven by the action button.
                 */
                <ReactFlowProvider>
                  <GraphCanvas
                    nodes={effectiveGraphSnapshot!.nodes}
                    edges={effectiveGraphSnapshot!.edges}
                    onNodesChange={noopNodesChange}
                    onEdgesChange={noopEdgesChange}
                    depth={0}
                    onDepthUp={noopDepth}
                    onDepthDown={noopDepth}
                    readOnly
                    presentationOnly
                    nodeOrientation="vertical"
                    locale={graphLocale}
                    rootNodeId={effectiveGraphSnapshot!.rootNodeId ?? null}
                  />
                </ReactFlowProvider>
              ) : (
                <GraphCanvasSkeleton
                  ariaLabel={graphMessage(graphLocale, "loadingGraph")}
                />
              )}
            </div>
          ) : null}
        </article>
      );
    }

    return (
      <>
        <EntityCard
          ref={ref}
          entity={collection}
          entityType="collection"
          renderTags={renderTags}
          renderAdditionalContent={renderAdditionalContent}
          renderEditFields={renderEditFields}
          renderPrimaryContent={renderPrimaryContent}
          hideTitle={!collection.title || collection.title.trim() === "" || collection.title.trim() === "[no_title]"}
          descriptionPlaceholder={ui.bodyPlaceholder}
          canEditPrivacy={true}
          canEditTitle={true}
          {...restProps}
          onEdit={collectionType === "graph" ? handleEdit : restProps.onEdit}
        />
        {collectionType === "graph" ? (
          <GraphFullscreenViewer
            isOpen={isFullscreenViewerOpen}
            onClose={() => setFullscreenViewerOpen(false)}
            collectionId={collection.id}
            initialNodes={effectiveGraphSnapshot?.nodes}
            initialEdges={effectiveGraphSnapshot?.edges}
            initialRootNodeId={effectiveGraphSnapshot?.rootNodeId ?? null}
            title={collection.title ?? null}
            locale={graphLocale}
          />
        ) : null}
      </>
    );
  }
);

CollectionCardRefactored.displayName = "CollectionCardRefactored";

export default CollectionCardRefactored;
